import { notification } from "antd";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiBuilding } from "react-icons/bi";
import { MdAdd, MdCalendarMonth, MdMailLock, MdPhone } from "react-icons/md";
import Button from "shared/components/Button";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { SET_ENTERPRISE_ACTIVE } from "utils/api/graphql/mutations/organizations";
import { DATE_FORMAT, ORGANIZATION_TYPES, USER_TYPES } from "utils/constants";
import { displayDate } from "utils/helpers/date";
import {
  isEnterpriseActive,
  nextEnterpriseActive,
} from "../enterpriseActivity";
import CreateEnterpriseForm from "./CreateEnterpriseForm";

export default function Enterprises({
  onDetail,
  data = [],
  count,
  showCreateForm,
  setShowCreateForm,
  loading,
  fetchMore,
  refetch,
  take,
}) {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.COMPANY);
  const LIST = t("LIST", { returnObjects: true });
  const ACTIVITY = t("ACTIVITY", { returnObjects: true });
  const [pendingId, setPendingId] = useState(null);
  const [setEnterpriseActive] = useMutation(SET_ENTERPRISE_ACTIVE);

  const toggleOrganization = async (event, organization) => {
    event.stopPropagation();
    setPendingId(organization.id);
    try {
      const { errors } = await setEnterpriseActive({
        variables: {
          where: { id: organization.id },
          data: { active: nextEnterpriseActive(organization) },
        },
      });
      if (errors) {
        notification.error({ message: ACTIVITY.ERROR });
        return;
      }
      if (refetch) await refetch();
    } catch (_error) {
      notification.error({ message: ACTIVITY.ERROR });
    } finally {
      setPendingId(null);
    }
  };

  const loadMoreOrganizations = () => {
    const currentLength = data.length;
    fetchMore({
      variables: {
        skip: currentLength,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.organizations?.data?.length) {
          return previousResult;
        }

        const existingIds = new Set(
          previousResult.organizations.data.map((item) => item.id),
        );
        const newOrganizations = fetchMoreResult.organizations.data.filter(
          (item) => !existingIds.has(item.id),
        );

        return {
          ...previousResult,
          organizations: {
            ...previousResult.organizations,
            data: [...previousResult.organizations.data, ...newOrganizations],
          },
        };
      },
    });
  };

  return (
    <div>
      {showCreateForm && (
        <CreateEnterpriseForm
          onClose={() => setShowCreateForm(false)}
          onCreated={() => {
            refetch();
            setShowCreateForm(false);
          }}
        />
      )}

      {data.length === 0 ? (
        <div className="company__bg-white company__rounded-lg company__border company__border-slate-200 company__p-12 company__text-center company__flex company__flex-col company__items-center company__justify-center">
          <BiBuilding className="company__w-12 company__h-12 company__text-slate-400 company__mx-auto company__mb-4" />
          <h3 className="company__text-lg company__font-medium company__text-slate-900 company__mb-2">
            {LIST.EMPTY.TITLE}
          </h3>
          <p className="company__text-slate-600 company__mb-6">
            {LIST.EMPTY.DESCRIPTION}
          </p>
          <Button type="primary" onClick={() => setShowCreateForm(true)}>
            <MdAdd />
            {LIST.EMPTY.ACTION}
          </Button>
        </div>
      ) : (
        <div className="company__grid company__grid-cols-1 company__gap-4">
          {data.map((organization) => {
            const email = organization.users.find(
              (user) =>
                get(user, "meta.type") === USER_TYPES.HUMAN_RESOURCE.type,
            )?.email;
            const phone = organization.users.find(
              (user) =>
                get(user, "meta.type") === USER_TYPES.HUMAN_RESOURCE.type,
            )?.phone;
            return (
              <div
                key={organization.id}
                role="button"
                tabIndex={0}
                onClick={() => onDetail && onDetail(organization.id)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  if (event.target !== event.currentTarget) return;
                  event.preventDefault();
                  if (onDetail) onDetail(organization.id);
                }}
                className="company__bg-white company__rounded-lg company__border company__border-slate-200 company__p-6 company__text-left company__hover-border-blue-300 company__hover-shadow-sm company__transition-all"
              >
                <div className="company__flex company__items-start company__justify-between">
                  <div className="company__flex company__gap-4">
                    {organization.icon ? (
                      <img
                        src={organization.icon}
                        alt={organization.name}
                        className="company__w-12 company__h-12 company__rounded-lg"
                        style={{ objectFit: "cover" }}
                      />
                    ) : (
                      <div className="company__w-12 company__h-12 company__bg-slate-100 company__rounded-lg company__flex company__items-center company__justify-center">
                        <BiBuilding className="company__w-6 company__h-6 company__text-slate-400" />
                      </div>
                    )}
                    <div>
                      <div className="company__flex company__items-center company__gap-2">
                        <h3 className="company__text-lg company__font-semibold company__text-slate-900">
                          {organization.name}
                        </h3>
                        <span
                          className={`company__px-2 company__py-1 company__text-xs company__font-medium company__rounded ${
                            organization.type === ORGANIZATION_TYPES.CDG
                              ? "company__bg-green-100 company__text-green-700"
                              : "company__bg-blue-100 company__text-blue-700"
                          }`}
                        >
                          {organization.type === ORGANIZATION_TYPES.CDG
                            ? LIST.TYPES.CDG
                            : LIST.TYPES.PRIVATE}
                        </span>
                      </div>
                      {organization.siretNumber && (
                        <p className="company__text-sm company__text-slate-600 company__mt-1">
                          {LIST.SIRET}: {organization.siretNumber}
                        </p>
                      )}
                      <div className="company__flex company__items-center company__gap-4 company__mt-3 company__text-sm company__text-slate-600">
                        {email && (
                          <span className="company__flex company__items-center company__gap-2">
                            <MdMailLock className="company__w-4 company__h-4" />
                            {email}
                          </span>
                        )}
                        {phone && (
                          <span className="company__flex company__items-center company__gap-2">
                            <MdPhone className="company__w-4 company__h-4" />
                            {phone}
                          </span>
                        )}
                        {organization?.counterpartyContracts[0]
                          ?.issuanceDate && (
                          <span className="company__flex company__items-center company__gap-2">
                            <MdCalendarMonth className="company__w-4 company__h-4" />
                            {displayDate({
                              date: organization?.counterpartyContracts[0]
                                ?.issuanceDate,
                              format: DATE_FORMAT,
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="company__flex company__items-center company__gap-2">
                    <span
                      className={`company__px-3 company__py-1 company__rounded-full company__text-xs company__font-medium ${
                        isEnterpriseActive(organization)
                          ? "company__bg-green-50 company__text-green-700"
                          : "company__bg-slate-100 company__text-slate-600"
                      }`}
                    >
                      {t(
                        `STATUS.${
                          isEnterpriseActive(organization)
                            ? "ACTIVE"
                            : "INACTIVE"
                        }`,
                      )}
                    </span>
                    <span
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <Button
                        type="secondary--link"
                        htmlType="button"
                        disabled={pendingId === organization.id}
                        onClick={(event) =>
                          toggleOrganization(event, organization)
                        }
                      >
                        {isEnterpriseActive(organization)
                          ? ACTIVITY.DEACTIVATE
                          : ACTIVITY.ACTIVATE}
                      </Button>
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          {!loading && data.length < count && (
            <div className="company__flex company__justify-center company__mt-6">
              <Button
                type="primary--link"
                onClick={loadMoreOrganizations}
                disabled={loading}
              >
                {loading ? LIST.LOADING : LIST.LOAD_MORE}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
